import { motion } from "framer-motion";
import { Gift, Sparkles, Mail, Truck } from "lucide-react";
import { Eyebrow, SerifH } from "./shared";

const FEATURES = [
  {
    Icon: Gift,
    label: "Gift-ready, always",
    body: "Every jellaba arrives dressed. Even the one you buy for yourself feels like a gift.",
  },
  {
    Icon: Sparkles,
    label: "Matte navy · brass foil",
    body: "Rigid book-fold mailer, matte navy lid, brushed brass sides + ribbon pull, hot-foil YABA inside.",
  },
  {
    Icon: Mail,
    label: "Hand-signed card",
    body: "Colorplan Imperial Blue card, brass foil YABA, serialised № ___ / 1000.",
  },
  {
    Icon: Truck,
    label: "Ships letterbox-flat",
    body: "3 cm slim so it slides through the postbox — no failed delivery, no sorry-we-missed-you notes.",
  },
];

export function PackagingShowcase() {
  return (
    <section
      id="packaging"
      data-testid="packaging-showcase"
      className="bg-[#EDE2CE] text-[#0C111C]"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-center">
        {/* Still image — no video, no flicker */}
        <motion.figure
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="packaging-image"
          className="w-full max-w-[520px] justify-self-center lg:justify-self-start"
        >
          <img
            src="/yaba_packaging.png"
            alt="YABA packaging — navy book-fold box with brass foil YABA wordmark"
            loading="lazy"
            className="w-full h-auto object-contain drop-shadow-[0_30px_50px_rgba(12,17,28,0.20)]"
          />
        </motion.figure>

        {/* Copy */}
        <div>
          <Eyebrow>The unboxing</Eyebrow>
          <SerifH className="mt-3" size="clamp(30px, 3.6vw, 52px)">
            Made to be given.
          </SerifH>
          <p
            className="mt-5 text-[#3A3A3E] text-base md:text-lg max-w-lg"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            We spent months on the packaging so the moment of opening is worth the wait.
            Matte navy and brushed brass, cream tissue sealed with a brass foil YABA disc —
            the piece you'd normally throw away, made to keep.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            {FEATURES.map(({ Icon, label, body }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`packaging-feature-${i}`}
                className="flex items-start gap-3"
              >
                <span
                  aria-hidden="true"
                  className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0C182E]/[0.05] ring-1 ring-[#0C182E]/15"
                >
                  <Icon size={16} strokeWidth={1.5} className="text-[#0C182E]" />
                </span>
                <div>
                  <p
                    className="text-base leading-tight"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {label}
                  </p>
                  <p
                    className="mt-1 text-sm leading-relaxed text-[#3A3A3E]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <p
            className="mt-8 text-[11px] tracking-[0.22em] uppercase text-[#C8A96A]"
            style={{ fontFamily: "Inter, sans-serif" }}
            data-testid="packaging-note"
          >
            Included with every order · No gift-wrap fee, ever.
          </p>
        </div>
      </div>
    </section>
  );
}
